import * as React from 'react'
import { Checkbox } from '@/components/ui/checkbox'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Textarea } from '@/components/ui/textarea'

export interface RegistrationQuestion {
  id: number
  prompt: string
  kind: 'short_text' | 'long_text' | 'single_choice' | 'multiple_choice' | 'yes_no'
  required: boolean
  scope: 'per_order' | 'per_attendee'
  options: string[]
}

export type RegistrationAnswer = string | string[]

export function RegistrationQuestionFields({
  questions,
  answers,
  onAnswer,
  attendeeQuestions = [],
  attendeeAnswers = [],
  attendeeLabel = 'Attendee',
  attendeeLabels,
  onAttendeeAnswer,
}: {
  questions: RegistrationQuestion[]
  answers: Record<number, RegistrationAnswer>
  onAnswer: (questionId: number, value: RegistrationAnswer) => void
  attendeeQuestions?: RegistrationQuestion[]
  attendeeAnswers?: Record<number, RegistrationAnswer>[]
  attendeeLabel?: string
  attendeeLabels?: string[]
  onAttendeeAnswer?: (attendeeIndex: number, questionId: number, value: RegistrationAnswer) => void
}) {
  if (questions.length === 0 && attendeeQuestions.length === 0) return null

  return (
    <section className="border-t border-border px-4 py-4" aria-labelledby="event-questions-heading">
      <h2 id="event-questions-heading" className="mb-3 text-body-sm font-medium">
        Questions
      </h2>
      <div className="space-y-3">
        {questions.map((question) => (
          <QuestionField
            key={question.id}
            question={question}
            value={answers[question.id]}
            onAnswer={(value) => onAnswer(question.id, value)}
          />
        ))}
        {attendeeAnswers.map((answersForAttendee, attendeeIndex) =>
          attendeeQuestions.map((question) => (
            <QuestionField
              key={`${attendeeIndex}-${question.id}`}
              question={{
                ...question,
                prompt: `${attendeeLabels?.[attendeeIndex] ?? attendeeLabel} — seat ${attendeeIndex + 1} · ${question.prompt}`,
              }}
              idSuffix={`attendee-${attendeeIndex}`}
              value={answersForAttendee[question.id]}
              onAnswer={(value) => onAttendeeAnswer?.(attendeeIndex, question.id, value)}
            />
          )),
        )}
      </div>
    </section>
  )
}

function QuestionField({
  question,
  value,
  onAnswer,
  idSuffix,
}: {
  question: RegistrationQuestion
  value: RegistrationAnswer | undefined
  onAnswer: (value: RegistrationAnswer) => void
  idSuffix?: string
}) {
  const id = `event-question-${question.id}${idSuffix ? `-${idSuffix}` : ''}`
  if (question.kind === 'long_text') {
    return (
      <Field label={question.prompt} id={id}>
        <Textarea
          id={id}
          rows={3}
          value={typeof value === 'string' ? value : ''}
          required={question.required}
          onChange={(event) => onAnswer(event.target.value)}
        />
      </Field>
    )
  }
  if (question.kind === 'single_choice' || question.kind === 'yes_no') {
    const options = question.kind === 'yes_no' ? ['Yes', 'No'] : question.options
    return (
      <Field label={question.prompt} id={id}>
        <Select value={typeof value === 'string' ? value : ''} onValueChange={onAnswer}>
          <SelectTrigger id={id} className="h-8 bg-background">
            <SelectValue placeholder="Select an answer" />
          </SelectTrigger>
          <SelectContent>
            {options.map((option) => (
              <SelectItem key={option} value={option}>
                {option}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </Field>
    )
  }
  if (question.kind === 'multiple_choice') {
    const selected = Array.isArray(value) ? value : []
    return (
      <fieldset className="space-y-2">
        <legend className="text-caption text-muted-foreground">{question.prompt}</legend>
        {question.options.map((option) => (
          <div key={option} className="flex items-center gap-2">
            <Checkbox
              id={`${id}-${option}`}
              checked={selected.includes(option)}
              onCheckedChange={(checked) =>
                onAnswer(
                  checked ? [...selected, option] : selected.filter((item) => item !== option),
                )
              }
            />
            <Label htmlFor={`${id}-${option}`} className="text-body-sm">
              {option}
            </Label>
          </div>
        ))}
      </fieldset>
    )
  }
  return (
    <Field label={question.prompt} id={id}>
      <Input
        id={id}
        className="h-8 bg-background"
        value={typeof value === 'string' ? value : ''}
        required={question.required}
        onChange={(event) => onAnswer(event.target.value)}
      />
    </Field>
  )
}

function Field({ label, id, children }: { label: string; id: string; children: React.ReactNode }) {
  return (
    <div className="space-y-1.5">
      <Label htmlFor={id} className="text-caption text-muted-foreground">
        {label}
      </Label>
      {children}
    </div>
  )
}
